import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import TradingToolSearchSelect from "@/components/dashboard/trading-tools/TradingToolSearchSelect";
import {
  calculateLotSize,
  DEPOSIT_CURRENCIES,
  formatMoney,
  LOT_INSTRUMENTS,
} from "@/data/lotSizeCalculator";
import type { LotSizeResult } from "@/types/tradingTool";
import { cn } from "@/utils/utils";

const FIELD_CLASS =
  "support-contact-field w-full rounded-[8px] border border-white/10 bg-[#121212] px-3 py-2 font-neue text-sm text-white placeholder:text-white/40 focus:outline-none";

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1.5 block font-neue text-sm text-white/80">
      {children}
    </label>
  );
}

function ResultMetric({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className="font-neue text-xs text-white/80 md:text-sm">{label}</p>
      <p className="mt-1 font-neue text-xl font-medium text-white md:text-2xl">
        {value}
      </p>
    </div>
  );
}

export default function LotSizeCalculatorView() {
  const { t } = useTranslation();
  const [instrumentId, setInstrumentId] = useState(LOT_INSTRUMENTS[0]?.id ?? "");
  const [depositCurrency, setDepositCurrency] = useState("USD");
  const [accountBalance, setAccountBalance] = useState("50000");
  const [contractSize, setContractSize] = useState(
    String(LOT_INSTRUMENTS[0]?.contractSize ?? 100000),
  );
  const [pipSize, setPipSize] = useState(String(LOT_INSTRUMENTS[0]?.pipSize ?? 0.00001));
  const [stopLossPips, setStopLossPips] = useState("250");
  const [riskAmount, setRiskAmount] = useState("500");
  const [results, setResults] = useState<LotSizeResult | null>(null);

  const selectedInstrument = useMemo(
    () => LOT_INSTRUMENTS.find((item) => item.id === instrumentId),
    [instrumentId],
  );

  const instrumentOptions = useMemo(
    () =>
      LOT_INSTRUMENTS.map((item) => ({
        value: item.id,
        label: item.label,
      })),
    [],
  );

  const currencyOptions = useMemo(
    () =>
      DEPOSIT_CURRENCIES.map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [],
  );

  const handleInstrumentChange = (value: string) => {
    setInstrumentId(value);
    const instrument = LOT_INSTRUMENTS.find((item) => item.id === value);
    if (!instrument) return;
    setContractSize(String(instrument.contractSize));
    setPipSize(String(instrument.pipSize));
    setResults(null);
  };

  const handleCalculate = () => {
    const nextResults = calculateLotSize({
      accountBalance: Number(accountBalance),
      contractSize: Number(contractSize),
      pipSize: Number(pipSize),
      stopLossPips: Number(stopLossPips),
      riskAmount: Number(riskAmount),
      leverage: selectedInstrument?.leverage ?? 30,
    });

    setResults(nextResults);
  };

  const pipLabel = selectedInstrument
    ? t("{{base}} 1 Pip Size", { base: selectedInstrument.base })
    : t("1 Pip Size");

  return (
    <div className="min-h-full w-full overflow-y-auto hide-scrollbar bg-[#050505] p-5">
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="card-gradient-hover !block rounded-2xl p-[1.5px]">
          <div className="rounded-[14px] bg-[#0b0b0b] p-5 md:p-6">
            <div className="space-y-4">
              <div>
                <FieldLabel>{t("Instrument")}</FieldLabel>
                <TradingToolSearchSelect
                  value={instrumentId}
                  options={instrumentOptions}
                  onChange={handleInstrumentChange}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel>{t("Deposit Currency")}</FieldLabel>
                  <TradingToolSearchSelect
                    value={depositCurrency}
                    options={currencyOptions}
                    onChange={setDepositCurrency}
                  />
                </div>
                <div>
                  <FieldLabel>{t("Account Balance")}</FieldLabel>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={accountBalance}
                    onChange={(event) => {
                      setAccountBalance(event.target.value);
                      setResults(null);
                    }}
                    placeholder={t("Account Balance")}
                    className={FIELD_CLASS}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel>{t("Contract Size")}</FieldLabel>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={contractSize}
                    onChange={(event) => {
                      setContractSize(event.target.value);
                      setResults(null);
                    }}
                    placeholder={t("Units per lot")}
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <FieldLabel>{pipLabel}</FieldLabel>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={pipSize}
                    onChange={(event) => {
                      setPipSize(event.target.value);
                      setResults(null);
                    }}
                    className={FIELD_CLASS}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel>{t("Stop Loss (Pips)")}</FieldLabel>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={stopLossPips}
                    onChange={(event) => {
                      setStopLossPips(event.target.value);
                      setResults(null);
                    }}
                    placeholder={t("Stop Loss (Pips)")}
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <FieldLabel>{t("Risk Amount")}</FieldLabel>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={riskAmount}
                    onChange={(event) => {
                      setRiskAmount(event.target.value);
                      setResults(null);
                    }}
                    placeholder={t("Risk Amount")}
                    className={FIELD_CLASS}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleCalculate}
                className="btn-gradient-left flex w-full items-center justify-center gap-2 rounded-[10px] px-4 py-2 font-neue text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                {t("Calculate")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </section>

        <section className="card-gradient !block rounded-2xl p-[1.5px]">
          <div className="relative overflow-hidden rounded-[14px] p-5 ">
            <div className="relative">
              <h2 className="font-neue text-base font-medium text-white md:text-lg">
                {t("Results")}
              </h2>

              <div className="mt-5 border-b border-white/10 pb-5">
                <p className="font-neue text-sm text-white/80">
                  {t("Lots (trade size)")}
                </p>
                <p className="mt-2 font-neue text-xl font-medium tracking-tight text-white md:text-2xl">
                  {results ? results.lots.toFixed(2) : "—"}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-5 border-b border-white/10 py-5 md:grid-cols-2">
                <ResultMetric
                  label={t("Margin required")}
                  value={
                    results ? formatMoney(results.marginRequired) : "—"
                  }
                />
                <ResultMetric
                  label={t("Free margin after")}
                  value={
                    results ? formatMoney(results.freeMarginAfter) : "—"
                  }
                />
              </div>

              <div className="grid grid-cols-1 gap-5 pt-5 md:grid-cols-2">
                <ResultMetric
                  label={t("Risk % of balance")}
                  value={results ? `${results.riskPercent.toFixed(2)}%` : "—"}
                />
                <ResultMetric
                  label={t("Max loss (SL hit)")}
                  value={results ? formatMoney(results.maxLoss) : "—"}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
